import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { BoltIcon, SparkleIcon } from './composerIcons';
import { composerStyles as styles } from './composerStyles';
import type { ComposerReplyType } from './types';

interface ComposerFooterProps {
	replyType: ComposerReplyType;
	/** Replies can't be sent for this ticket (no customer assigned yet). Notes/forwards are unaffected. */
	replyDisabled: boolean;
	isSending: boolean;
	hasUploading: boolean;
	hasContent: boolean;
	forwardToValue: string;
	showSendAndClose: boolean;
	onSend: () => void;
	onSendAndClose: () => void;
	draftLabel: string;
	savedRepliesActive: boolean;
	savedRepliesBtnRef: React.RefObject<HTMLButtonElement>;
	onToggleSavedReplies: () => void;
	showAi: boolean;
	aiMenuOpen: boolean;
	aiMenuRef: React.RefObject<HTMLDivElement>;
	onToggleAiMenu: () => void;
	aiBusy: boolean;
	onRefine: () => void;
	onReview: () => void;
	onDraft: () => void;
}

const SEND_LABELS: Record<ComposerReplyType, string> = {
	forward: 'Forward',
	note: 'Add note',
	reply: 'Send',
};

const AI_MENU_ITEMS = [
	{ label: 'Refine draft', description: 'Tighten wording, keep your intent', action: 'refine' as const },
	{ label: 'Review & rewrite', description: 'Full pass — tone, structure, accuracy', action: 'review' as const },
	{ label: 'Draft reply', description: 'Write a reply from ticket context', action: 'draft' as const },
];

/**
 * The composer's send row: primary send, "Send & Close", the draft status label, and the
 * saved-replies / attach / AI-assist icons with the AI actions popover.
 */
export const ComposerFooter: React.FC<ComposerFooterProps> = ({
	replyType,
	replyDisabled,
	isSending,
	hasUploading,
	hasContent,
	forwardToValue,
	showSendAndClose,
	onSend,
	onSendAndClose,
	draftLabel,
	savedRepliesActive,
	savedRepliesBtnRef,
	onToggleSavedReplies,
	showAi,
	aiMenuOpen,
	aiMenuRef,
	onToggleAiMenu,
	aiBusy,
	onRefine,
	onReview,
	onDraft,
}) => {
	const sendDisabled =
		isSending ||
		hasUploading ||
		(replyType === 'forward' ? !forwardToValue.trim() : !hasContent) ||
		(replyType === 'reply' && replyDisabled);
	const runAiAction = { refine: onRefine, review: onReview, draft: onDraft };

	return (
		<div className={css(styles.footer)}>
			<Button
				variant='primary'
				size='sm'
				styles={[styles.sendBtn]}
				isLoading={isSending}
				onClick={onSend}
				disabled={sendDisabled}
			>
				{SEND_LABELS[replyType]}
			</Button>
			{replyType === 'reply' && showSendAndClose && (
				<Button
					variant='outline'
					size='sm'
					styles={[styles.sendCloseBtn]}
					onClick={onSendAndClose}
					disabled={sendDisabled}
				>
					Send &amp; Close
				</Button>
			)}
			<div className={css(styles.footerSpacer)} />
			{draftLabel && <span className={css(styles.draftLabel)}>{draftLabel}</span>}
			<div className={css(styles.footerIcons)}>
				<Button
					ref={savedRepliesBtnRef}
					variant='ghost'
					size='sm'
					styles={[styles.footerIconBtn, ...(savedRepliesActive ? [styles.footerIconBtnActive] : [])]}
					onMouseDown={event => event.preventDefault()}
					onClick={onToggleSavedReplies}
					title='Saved replies (or type /)'
					aria-label='Saved replies'
				>
					<BoltIcon />
				</Button>
				{showAi && (
					<Button
						variant='ghost'
						size='sm'
						styles={[styles.footerIconBtn]}
						onClick={onToggleAiMenu}
						title='AI assist'
						aria-label='AI assist'
					>
						<SparkleIcon />
					</Button>
				)}
			</div>
			{aiMenuOpen && (
				<div ref={aiMenuRef} className={css(styles.aiMenu)}>
					{AI_MENU_ITEMS.map(item => (
						<Button
							key={item.action}
							variant='ghost'
							size='sm'
							styles={[styles.aiMenuItem]}
							onClick={runAiAction[item.action]}
							disabled={aiBusy}
						>
							<span className={css(styles.aiMenuItemLabel)}>{item.label}</span>
							<span className={css(styles.aiMenuItemDesc)}>{item.description}</span>
						</Button>
					))}
				</div>
			)}
		</div>
	);
};
